/**
 * Command for converting MText to MLeader with undo/redo support.
 *
 * Execute: Soft-delete MText (move to undo layer), create MLeader
 * Undo: Delete MLeader, restore MText from undo layer
 * Redo: Soft-delete MText again, restore MLeader
 */

import type { ICommand } from "./ICommand";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { openAs, findEntityByHandle } from "../core/oda";
import { OpenMode } from "../core/constants";
import { UNDO_LAYER_NAME, ensureUndoLayerExists } from "../database/layers";
import {
  createMLeaderFromMText,
  type CreateMLeaderFromMTextOptions,
} from "../leaders/conversion";
import { recordGraphicsModified } from "../entities";
import { selectionService } from "../selection";
import { transferMetaOnConvert } from "../meta/capture";

export interface ConvertMTextToMLeaderParams {
  /** Handle of the MText entity to convert */
  mtextHandle: string;
  /** Original layer name of the MText */
  originalLayerName: string;
  /** Options for creating the MLeader */
  mleaderOptions: CreateMLeaderFromMTextOptions;
}

/**
 * Command to convert MText to MLeader.
 * Supports undo/redo by using soft-delete (layer switching).
 */
export class ConvertMTextToMLeaderCommand implements ICommand {
  private viewer: DRDrawingViewer;
  private params: ConvertMTextToMLeaderParams;
  private executed = false;

  /** Handle of the created MLeader (stored for undo/redo) */
  private mleaderHandle: string | null = null;

  constructor(viewer: DRDrawingViewer, params: ConvertMTextToMLeaderParams) {
    this.viewer = viewer;
    this.params = params;
  }

  getLeaderHandle(): string | null {
    return this.mleaderHandle;
  }

  /**
   * Execute: Soft-delete MText, create MLeader.
   */
  execute(): void {
    if (this.executed) return;

    const db = this.viewer.database;
    if (!db) return;

    // Ensure undo layer exists
    ensureUndoLayerExists(this.viewer.module, db);

    // Find MText by handle
    const mtextId = findEntityByHandle(
      this.viewer.module,
      db,
      this.params.mtextHandle,
    );
    if (!mtextId) {
      console.error(
        "[ConvertCommand] MText not found by handle:",
        this.params.mtextHandle,
      );
      return;
    }

    // Move MText to undo layer (soft delete)
    this.setEntityLayer(mtextId, UNDO_LAYER_NAME);
    recordGraphicsModified(this.viewer.module, mtextId);
    // unselect
    const isSelected = selectionService.has(mtextId);
    if (isSelected) {
      selectionService.remove(mtextId);
    }
    selectionService.remove(mtextId);
    deleteObj(mtextId);

    // Create MLeader
    const mleaderId = createMLeaderFromMText(
      this.viewer,
      this.params.mleaderOptions,
    );
    if (mleaderId) {
      this.mleaderHandle = this.getEntityHandle(mleaderId);
      recordGraphicsModified(this.viewer.module, mleaderId);
      // Le MLeader hérite du DR_META du texte (identité + observation) — sinon il
      // apparaîtrait comme un nouvel élément non groupé dans le fil « Activité ».
      transferMetaOnConvert(
        this.viewer,
        this.params.mtextHandle,
        this.mleaderHandle,
      );
      if (isSelected) {
        selectionService.add(mleaderId);
      }
      deleteObj(mleaderId);
    }

    this.executed = true;
    this.viewer.update();
  }

  /**
   * Undo: Delete MLeader, restore MText.
   */
  undo(): void {
    if (!this.executed) return;

    const db = this.viewer.database;
    if (!db) return;

    const module = this.viewer.module;

    // Soft-delete MLeader (move to undo layer)

    if (this.mleaderHandle) {
      // Use resolveCurrentHandle in case the MLeader was recreated
      const mleaderId = findEntityByHandle(module, db, this.mleaderHandle);
      if (mleaderId) {
        // Push leader to undo layer (hide it)
        this.setEntityLayer(mleaderId, UNDO_LAYER_NAME);

        const isSelected = selectionService.has(mleaderId);
        recordGraphicsModified(module, mleaderId);
        if (isSelected) {
          selectionService.remove(mleaderId);
        }
        deleteObj(mleaderId);

        // Restore MText from undo layer
        const mtextId = findEntityByHandle(module, db, this.params.mtextHandle);
        if (mtextId) {
          // Push back entity to original layer
          this.setEntityLayer(mtextId, this.params.originalLayerName);
          recordGraphicsModified(module, mtextId);

          // select  if leader was selected
          if (isSelected) {
            selectionService.add(mtextId);
          }
          deleteObj(mtextId);
        }
      }
    }

    this.executed = false;
    this.viewer.update();
  }

  /**
   * Redo: Soft-delete MText, restore MLeader.
   */
  redo(): void {
    if (this.executed) return;

    const db = this.viewer.database;
    if (!db) return;

    const module = this.viewer.module;

    // Soft-delete MText again
    const mtextId = findEntityByHandle(module, db, this.params.mtextHandle);
    if (mtextId) {
      // hide text again
      this.setEntityLayer(mtextId, UNDO_LAYER_NAME);
      recordGraphicsModified(module, mtextId);
      // unselect
      const isSelected = selectionService.has(mtextId);
      if (isSelected) {
        selectionService.remove(mtextId);
      }
      deleteObj(mtextId);

      // Restore MLeader from undo layer
      if (this.mleaderHandle) {
        // Use resolveCurrentHandle in case the MLeader was recreated
        const currentHandle = this.mleaderHandle;
        const mleaderId = findEntityByHandle(module, db, currentHandle);
        if (mleaderId) {
          // show leader again
          this.setEntityLayer(mleaderId, this.params.originalLayerName);
          recordGraphicsModified(module, mleaderId);
          // select mleader if text was selected
          if (isSelected) {
            selectionService.add(mleaderId);
          }
          deleteObj(mleaderId);
        }
      }
    }

    this.executed = true;
    this.viewer.update();
  }

  /**
   * Dispose: Clean up entities in undo layer.
   */
  dispose(): void {
    // if command is not exectued, meaning that it was undone, nothing to clear
    if (!this.executed) return;
    const db = this.viewer.database;
    if (!db) return;
    const handleToDelete = this.executed
      ? this.params.mtextHandle
      : this.mleaderHandle;
    if (handleToDelete) {
      const entityId = findEntityByHandle(
        this.viewer.module,
        db,
        handleToDelete,
      );
      if (entityId) {
        const entity = openAs(
          entityId,
          this.viewer.module.OdDbEntity,
          OpenMode.kForWrite,
        );
        const layerStr = entity.layer();
        const currentLayer = layerStr.toString();
        deleteObj(layerStr);

        if (currentLayer === UNDO_LAYER_NAME) {
          entity.erase(true);
        }
        deleteObj(entity);
      }
    }
  }

  // ============== Private Helpers ==============

  private setEntityLayer(
    entityId: DrawingModule.OdDbObjectId,
    layerName: string,
  ): void {
    const entity = openAs(
      entityId,
      this.viewer.module.OdDbEntity,
      OpenMode.kForWrite,
    );
    const module = this.viewer.module;
    const layerStr = new module.OdString();
    layerStr.set(layerName);
    entity.setLayer(layerStr, false, true);
    deleteObj(layerStr);
    deleteObj(entity);
  }

  private getEntityHandle(entityId: DrawingModule.OdDbObjectId): string {
    const handle = entityId.getHandle();
    const handleStr = handle.ascii();
    const result = handleStr.toString();
    deleteObj(handleStr);
    deleteObj(handle);
    return result;
  }
}
